import React, { useState, useCallback, FormEvent } from "react";
import {
  Box,
  StackLayout,
  HeadingText,
  ParagraphText,
  TextInput,
  InputLabel,
  Button,
  UtilityText,
} from "@leagueplatform/genesis-core";
import { Masonry } from "@leagueplatform/masonry";
import { useDocumentTitle } from "@leagueplatform/web-common";
import { CONTENT_MAX_WIDTH } from "@leagueplatform/web-common-components";

type QueryParams = Record<string, string>;

export const MasonryPlaygroundPage = () => {
  const [masonryNodeId, setMasonryNodeId] = useState("");
  const [masonryDriverQueryParams, setMasonryDriverQueryParams] =
    useState<QueryParams>({});
  const [nodeIdInput, setNodeIdInput] = useState("");
  const [queryParamKey, setQueryParamKey] = useState("");
  const [queryParamValue, setQueryParamValue] = useState("");

  useDocumentTitle("Masonry Playground");

  const queryParamKeys = Object.keys(masonryDriverQueryParams);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setMasonryNodeId(nodeIdInput);
  };

  const handleAddQueryParam = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (queryParamKey && queryParamValue) {
      setMasonryDriverQueryParams((prev) => ({
        ...prev,
        [queryParamKey]: queryParamValue,
      }));
      setQueryParamKey("");
      setQueryParamValue("");
    }
  };

  const handleRemoveQueryParam = useCallback((key: string) => {
    setMasonryDriverQueryParams((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  }, []);

  return (
    <StackLayout
      orientation="vertical"
      horizontalAlignment="stretch"
      css={{
        marginX: "auto",
        padding: "$two",
        maxWidth: CONTENT_MAX_WIDTH,
        "@mobile": {
          padding: "$one",
        },
      }}
    >
      {/* Header Section */}
      <Box css={{ paddingBlockEnd: "$two", textAlign: "center" }}>
        <HeadingText level="1" size="xxl" css={{ marginBlockEnd: "$one" }}>
          Masonry Container Playground
        </HeadingText>
        <ParagraphText css={{ color: "$onSurfaceTextSubdued" }}>
          Enter a Masonry Node ID and optional query parameters to view a
          dynamic container.
        </ParagraphText>
      </Box>

      {/* Divider with Label */}
      <StackLayout
        orientation="horizontal"
        verticalAlignment="center"
        css={{ marginBlock: "$two" }}
      >
        <Box
          css={{
            flex: 1,
            height: 1,
            backgroundColor: "$onSurfaceBorderSubdued",
          }}
        />
        <UtilityText
          css={{
            paddingInline: "$one",
            color: "$interactiveFocusInner",
            textTransform: "uppercase",
            fontWeight: "$semibold",
          }}
        >
          ↓ League View ↓
        </UtilityText>
        <Box
          css={{
            flex: 1,
            height: 1,
            backgroundColor: "$onSurfaceBorderSubdued",
          }}
        />
      </StackLayout>

      {/* Node ID Form */}
      <Box
        as="form"
        onSubmit={handleSubmit}
        css={{ marginBlockEnd: "$oneAndHalf" }}
      >
        <StackLayout
          orientation="horizontal"
          spacing="$half"
          verticalAlignment="bottom"
          css={{
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <Box css={{ minWidth: 280 }}>
            <InputLabel htmlFor="nodeId">Masonry Node ID</InputLabel>
            <TextInput
              id="nodeId"
              value={nodeIdInput}
              onChange={(e) => setNodeIdInput(e.target.value)}
              placeholder="Enter Masonry Node ID"
            />
          </Box>
          <Button
            type="submit"
            priority="primary"
            css={{ marginBlockEnd: "$quarter" }}
          >
            View Masonry Container
          </Button>
        </StackLayout>
      </Box>

      {/* Query Params Form */}
      <Box
        as="form"
        onSubmit={handleAddQueryParam}
        css={{ marginBlockEnd: "$oneAndHalf" }}
      >
        <StackLayout
          orientation="horizontal"
          spacing="$half"
          verticalAlignment="bottom"
          css={{
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <Box css={{ minWidth: 180 }}>
            <InputLabel htmlFor="queryParamKey">Query Param Key</InputLabel>
            <TextInput
              id="queryParamKey"
              value={queryParamKey}
              onChange={(e) => setQueryParamKey(e.target.value)}
              placeholder="Key"
            />
          </Box>
          <Box css={{ minWidth: 180 }}>
            <InputLabel htmlFor="queryParamValue">Query Param Value</InputLabel>
            <TextInput
              id="queryParamValue"
              value={queryParamValue}
              onChange={(e) => setQueryParamValue(e.target.value)}
              placeholder="Value"
            />
          </Box>
          <Button
            type="submit"
            priority="secondary"
            css={{ marginBlockEnd: "$quarter" }}
          >
            Add Query Param
          </Button>
        </StackLayout>
      </Box>

      {/* Query Params Display */}
      {queryParamKeys.length > 0 && (
        <Box css={{ marginBlockEnd: "$oneAndHalf" }}>
          <UtilityText emphasis="emphasized" css={{ marginBlockEnd: "$half" }}>
            Query Params:
          </UtilityText>
          <StackLayout
            orientation="horizontal"
            spacing="$half"
            css={{ flexWrap: "wrap" }}
          >
            {queryParamKeys.map((key) => (
              <Box
                key={key}
                css={{
                  display: "flex",
                  alignItems: "center",
                  gap: "$half",
                  backgroundColor: "$surfaceBackgroundSecondary",
                  paddingInline: "$threeQuarters",
                  paddingBlock: "$quarter",
                  borderRadius: "$small",
                }}
              >
                <UtilityText size="sm">
                  {key}: {masonryDriverQueryParams[key]}
                </UtilityText>
                <Button
                  priority="tertiary"
                  quiet
                  size="small"
                  onClick={() => handleRemoveQueryParam(key)}
                  css={{
                    minWidth: "auto",
                    padding: "$quarter",
                    color: "$criticalIcon",
                  }}
                >
                  ✕
                </Button>
              </Box>
            ))}
          </StackLayout>
        </Box>
      )}

      {/* Masonry Engine Container */}
      {masonryNodeId && (
        <Box
          css={{
            borderBlockStart: "2px solid $onSurfaceBorderDefault",
            paddingBlockStart: "$two",
          }}
        >
          <Masonry
            rootNodeId={masonryNodeId}
            initialQueryParams={masonryDriverQueryParams}
          />
        </Box>
      )}
    </StackLayout>
  );
};
